<template>
    <HeaderContent title="Database Backup"/>
    <ActionContent
        ctaText="Generate Backup"
        :filterDate="false"
        :filterModal="false"
        @action="generateBackup"
        @search-query="searchData"
    />
    <DataTable
        :data="backupList"
        :header="headers"
        :custom-actions="customActions"
    >
        <template #cell(size)="{ item }">
            {{ formatBytes(item.size) }}
        </template>
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from '@/components/HeaderContent.vue';
import ActionContent from '@/components/ActionContent.vue';
import DataTable from '@/components/DataTable.vue';
import { ref, onMounted, inject } from 'vue';
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";

const auth = useAuthStore();
const $loading = useLoading();
const swal = inject("$swal");
const toast = useToast();

const backupList = ref([]);

const headers = ref([
    { key: "no", label: "No", sortable: true, class: "text-center" },
    { key: "name", label: "File Name", sortable: true, class: "text-left" },
    { key: "size", label: "Size", sortable: true, class: "text-left" },
    { key: "date", label: "Date Created", sortable: true, class: "text-left" },
    { key: "action", label: "Actions", class: "text-center" },
]);

const customActions = ref([
    {
        label: "Download",
        variant: "primary",
        icon: "fas fa-download",
        action: (item) => {
            downloadBackup(item.name);
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this backup?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.name);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

const getAll = async (search = "") => {
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/backup`, {
            params: {
                search: search,
            },
            headers: {
                "Content-type": "application/json",
                Authorization: "Bearer " + auth._token,
            },
        });

        const res = response.data;
        if (res.status) {
            backupList.value = res.data;
        }
    } catch (error) {
        console.error("Failed to fetch backups", error);
    }
};

const generateBackup = async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/backup/generate`, {}, {
            headers: {
                "Content-type": "application/json",
                Authorization: "Bearer " + auth._token,
            },
        });
        
        const res = response.data;
        if (res.status) {
            toast.success(res.message);
            getAll();
        }
    } catch (error) {
        toast.error(error.response?.data?.message || "Failed to generate backup");
    } finally {
        loader.hide();
    }
};

const downloadBackup = async (filename) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/backup/download/${filename}`, {
            responseType: 'blob',
            headers: {
                Authorization: "Bearer " + auth._token,
            },
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } catch (error) {
        toast.error("Failed to download backup");
    } finally {
        loader.hide();
    }
};

const deleteData = async (filename) => {
    if (filename) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/backup/delete/${filename}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } catch (error) {
             toast.error(error.response?.data?.message || "Failed to delete backup");
        } finally {
            loader.hide();
        }
    }
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
